import React from 'react';
import VideoPlayer, { TimeUpdateProps } from '../../../../VideoPlayer/VideoPlayer';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../../reducers/user.selectors';
import service, { ampliServerSideEventTrack } from '../../../../../services/track.service';
import { trackingEvents } from '../../../../../services/tracking/trackConfig';
import { ReaderContentViewVariant } from '../../../../../types/reader';
import { isFeatureEnabled } from '../../../../../utils/feature';
import { getContentEventPayload } from '../../../../../utils/ampli';
import VideoNotAvailableCard from '../../../../cards/VideoNotAvailableCard/VideoNotAvailableCard';
import { amplitudeContentTypes, amplitudeEvents, } from '../../../../../constants';
import { AmplitudeTrackPayload } from '@klab-berlin/api-sdk/lib/types/requests/Track';
import { Box } from '@mui/material';
import VideoReaderBannerContainer from '../../../../VideoReaderBannerContainer';
import { spaces } from '../../../../../assets/styles/themes/tokens';

interface VideoPlayerVimeoProps {
  url: string;
  mindItemId: string;
  classYears: number[];
  schoolTypes: string[];
  subjects: string[];
  title: string;
  publisher: string;
  variant: ReaderContentViewVariant;
  setIsReady: (arg: boolean) => void;
  onPause: () => void;
  isPublic?: boolean;
}

const VideoPlayerVimeo: React.FC<VideoPlayerVimeoProps> =
  ({
    mindItemId,
    title,
    url, classYears,
    schoolTypes,
    subjects,
    publisher,
    setIsReady,
    onPause,
    variant,
    isPublic
  }) => {
    const user = useSelector(selectUser);
    const [showVideoReaderBanner, setShowVideoReaderBanner] = React.useState(false);
    const eventPayload = isPublic ?
      { other: { 'mind_item_id': mindItemId, user_type: 3 } } :
      { other: { 'mind_item_id': mindItemId } };

    const trackingFunction = () => !isPublic &&
        service.eventTrack(trackingEvents.reader.videoStreamed30Seconds, eventPayload);

    const hasDocumentLevelAccess = user && user.hasDocumentLevelAccess;
    
    const onPlay = () => {
      service.eventTrack(trackingEvents.reader.videoStart, eventPayload);
    };
    let is30SecondsAchieved = false;
    const onStreamed30Seconds = (data: TimeUpdateProps) => {
      const hasStreamedEnough = data?.seconds >= 30;
      if (!is30SecondsAchieved && (hasDocumentLevelAccess || isPublic) && hasStreamedEnough) {
        is30SecondsAchieved = true;
        trackingFunction();
        trackAmpliEvent();
      }

      if (hasStreamedEnough && !isPublic) setShowVideoReaderBanner(true);
    };

    const trackAmpliEvent = () => {
      const isPublicMindItem: boolean = isPublic || false;
      const mindItem = {
        title,
        classYears,
        schoolTypes,
        subjects,
        publisher,
        id: mindItemId,
        publicVideo: isPublicMindItem ? 'Yes': 'No'
      };

      const contentCompletedEventPayload: AmplitudeTrackPayload | null = getContentEventPayload({
        content: mindItem,
        event_type: amplitudeEvents.ContentCompleted,
        contentType: amplitudeContentTypes.video
      });

      if (contentCompletedEventPayload) {
        ampliServerSideEventTrack(isPublicMindItem, contentCompletedEventPayload);
      }
    };

    const renderVideoPlayer = () => {
      if (isPublic) {
        return (
          <VideoPlayer
            url={url}
            onPlay={onPlay}
            onStreamed={onStreamed30Seconds}
          />
        );
      } else {
        if (isFeatureEnabled('vimeo')) {
          return (
            <Box
              data-testid='video-player-vimeo-container'
              sx={{
                position: 'relative',
                width: '100%',
              }}
            >
              <VideoReaderBannerContainer 
                open={showVideoReaderBanner}
                handleClose={() => setShowVideoReaderBanner(false)}
                user={user}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spaces.md,
                  width: '100%',
                  height: '100%'
                }}
              >
                <VideoPlayer
                  url={url}
                  onPause={onPause}
                  onPlay={onPlay}
                  onStreamed={onStreamed30Seconds}
                  onReady={(value) => setIsReady(value)}
                />
              </Box>       
            </Box>
          );
        } else {
          return (
            <Box sx={{
              width: '100%',
              height: '486px',
              ...(variant === 'mobile' && {
                marginTop: spaces.xxxxl,
              }),
            }}>
              <VideoNotAvailableCard />
            </Box>
          );
        }
      }

    };

    return renderVideoPlayer();
  };

export default VideoPlayerVimeo;
